/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useContext } from "react";

import { datadogLogs } from "@datadog/browser-logs";
import { serializeError } from "serialize-error";

import { AmplitudeEvents } from "../constants/amplitude";
import { RecaptchaAction } from "../constants/recaptcha";
import { StrongAuthContext, StrongAuthContextProps, StrongAuthModalSubmitData } from "../contexts/StrongAuthContext";
import { TwoFactorContext, TwoFactorContextProps, TwoFactorMethod } from "../contexts/TwoFactorContext";
import { getTelemetryID } from "../helpers/stytch";

import { useAmplitude } from "./amplitude/useAmplitude";
import { useRecaptchaRetry } from "./useRecaptchaRetry";

type UseSmsCodeMoment =
  | "bank"
  | "add treasury beneficiary"
  | "delete treasury beneficiary"
  | "transfer funds to beneficiary"
  | "onboard on power balance"
  | "onboard on stripe treasury"
  | "bank rights added to user"
  | "user with bank rights invited"
  | "ownership changed"
  | "login"
  | "show issuing card pin"
  | "update mfa preferences"
  | "email sending"
  | "activate mandatory 2fa"
  | "update phone number"
  | "update email"
  | "activate totp"
  | "reveal api key"
  | "regenerate api key"
  | "reveal webhook signing secret"
  | "regenerate webhook signing secret";

type StrongAuthChallenge = {
  code: string;
  message?: string;
  maskedPhoneNumber?: string;
  hasSmsFallback?: boolean;
  isSmsVerificationNext?: boolean;
};
type VerifiedResult<TData> = TData & { error?: { code: string; message?: string } | null; isCancelled?: boolean };

const createStrongAuthPrompts = <TData, TArgs>({
  mutateAsync,
  input,
  moment,
  logAmplitudeEvent,
  openTwoFactorModal,
  closeTwoFactorModal,
  openStrongAuthModal,
  closeStrongAuthModal,
}: {
  mutateAsync: (args: TArgs) => TData & { error?: StrongAuthChallenge };
  input: TArgs;
  moment: UseSmsCodeMoment;
  logAmplitudeEvent: ReturnType<typeof useAmplitude>["logAmplitudeEvent"];
} & Pick<TwoFactorContextProps, "openTwoFactorModal" | "closeTwoFactorModal"> &
  Pick<StrongAuthContextProps, "openStrongAuthModal" | "closeStrongAuthModal">) => {
  const cancelledResult = { data: null, error: null, isCancelled: true } as unknown as VerifiedResult<TData>;

  const askForTotpCode = (challenge: StrongAuthChallenge, challengeInput: TArgs) =>
    new Promise<VerifiedResult<TData>>((resolve) => {
      openTwoFactorModal({
        preferredMethod: "totp",
        hasSmsFallback: Boolean(challenge.hasSmsFallback),
        isSmsVerificationNext: Boolean(challenge.isSmsVerificationNext),
        maskedPhoneNumber: challenge.maskedPhoneNumber,
        onSubmit: async (code: string, method: TwoFactorMethod, options?: { askForBypassMfa?: boolean }) => {
          const result =
            method === "totp"
              ? await mutateAsync({ ...challengeInput, totpCode: code, smsCode: null, ...options })
              : await mutateAsync({ ...challengeInput, smsCode: code, totpCode: null, preferSms: true, ...options });

          if (result.error?.code === "sms_code_required") {
            return { switchToSms: { maskedPhoneNumber: result.error.maskedPhoneNumber } };
          }

          const incorrectCodeError =
            method === "totp" ? "authenticate_totp_code_incorrect" : "authenticate_sms_otp_code_incorrect";

          if (result.error?.code === incorrectCodeError) {
            return { error: result.error };
          }

          logAmplitudeEvent(AmplitudeEvents.TwoFASuccessful, { moment, method });
          closeTwoFactorModal();
          resolve(result);
          return null;
        },
        onRequestSmsFallback: async () => {
          // Re-call the mutation with preferSms flag to trigger SMS sending
          const smsResult = await mutateAsync({ ...challengeInput, preferSms: true, smsCode: null, totpCode: null });

          if (smsResult.error?.code === "sms_code_required") {
            return { maskedPhoneNumber: smsResult.error.maskedPhoneNumber };
          }

          if (smsResult.error) {
            return { error: smsResult.error.message || smsResult.error.code };
          }

          return null;
        },
        onCancel: () => {
          closeTwoFactorModal();
          resolve(cancelledResult);
        },
      });
    });

  const askForSmsCode = (challenge: StrongAuthChallenge, challengeInput: TArgs) =>
    new Promise<VerifiedResult<TData>>((resolve) => {
      openTwoFactorModal({
        preferredMethod: "sms",
        maskedPhoneNumber: challenge.maskedPhoneNumber,
        onSubmit: async (code: string, _method: TwoFactorMethod, options?: { askForBypassMfa?: boolean }) => {
          const result = await mutateAsync({ ...challengeInput, smsCode: code, totpCode: null, ...options });

          if (result.error?.code === "authenticate_sms_otp_code_incorrect") {
            return { error: result.error };
          }

          if (result.error?.code === "sms_code_required") {
            return { switchToSms: { maskedPhoneNumber: result.error.maskedPhoneNumber } };
          }

          logAmplitudeEvent(AmplitudeEvents.TwoFASuccessful, { moment, method: "sms" });
          closeTwoFactorModal();
          resolve(result);
          return null;
        },
        onCancel: () => {
          closeTwoFactorModal();
          resolve(cancelledResult);
        },
      });
    });

  const askForSecondFactor = (challenge: StrongAuthChallenge, challengeInput: TArgs) =>
    challenge.code === "totp_code_required"
      ? askForTotpCode(challenge, challengeInput)
      : askForSmsCode(challenge, challengeInput);

  const askForPassword = () =>
    new Promise<VerifiedResult<TData>>((resolve) => {
      openStrongAuthModal({
        onSubmit: async (data: StrongAuthModalSubmitData) => {
          const { password, googleSsoToken, askForBypassMfa } = data;
          const passwordInput = {
            ...input,
            password: password || null,
            googleSsoToken: googleSsoToken || null,
            askForBypassMfa,
          };
          const result = await mutateAsync(passwordInput);

          if (result.error?.code === "authenticate_on_stytch_unauthorized_credentials") {
            return { error: result.error };
          }

          logAmplitudeEvent(AmplitudeEvents.PasswordCheckSuccessful, { moment });
          closeStrongAuthModal();
          const nextChallenge = result.error;
          if (nextChallenge?.code === "totp_code_required" || nextChallenge?.code === "sms_code_required") {
            void askForSecondFactor(nextChallenge, { ...passwordInput, googleSsoToken: null }).then(resolve);
            return null;
          }
          resolve(result);
          return null;
        },
        onCancel: () => {
          closeStrongAuthModal();
          resolve(cancelledResult);
        },
      });
    });

  return { askForSecondFactor, askForPassword };
};

export const useProtectByStrongAuth = (moment: UseSmsCodeMoment) => {
  const { executeTrpc } = useRecaptchaRetry();
  const { logAmplitudeEvent } = useAmplitude();
  const { openStrongAuthModal, closeStrongAuthModal } = useContext(StrongAuthContext);
  const { openTwoFactorModal, closeTwoFactorModal } = useContext(TwoFactorContext);

  /**
   * Executes a tRPC mutation with 2FA (SMS/TOTP), password, or Google SSO protection.
   * 1. Fetches the Stytch telemetryId for Device Fingerprinting (fraud protection)
   * 2. Calls the mutation directly
   * 3. If response indicates TOTP is required (error code "totp_code_required"), shows TOTP modal
   * 4. If response indicates SMS is required (error code "sms_code_required"), shows SMS modal
   * 5. If response indicates password is required (error code "strong_auth_required"), shows password modal
   * 6. Re-executes the mutation with the verification code/password from the user
   */
  const mutateWithSmsCode = useCallback(
    async <TData, TArgs>(
      mutateAsync: (args: TArgs) => TData & { error?: StrongAuthChallenge },
      args: Omit<TArgs, "smsCode" | "password" | "googleSsoToken" | "totpCode" | "preferSms"> & {
        smsCode?: string | null;
        password?: string | null;
        googleSsoToken?: string | null;
        totpCode?: string | null;
        preferSms?: boolean | null;
      }
    ): Promise<VerifiedResult<TData>> => {
      try {
        // Fetch Stytch Device Fingerprint for toll fraud protection
        const telemetryId = await getTelemetryID();

        const { smsCode, password, googleSsoToken, totpCode } = args;
        const input = {
          ...args,
          smsCode: smsCode || null,
          password: password || null,
          googleSsoToken: googleSsoToken || null,
          totpCode: totpCode || null,
          telemetryId,
        } as TArgs;

        const { askForSecondFactor, askForPassword } = createStrongAuthPrompts({
          mutateAsync,
          input,
          moment,
          logAmplitudeEvent,
          openTwoFactorModal,
          closeTwoFactorModal,
          openStrongAuthModal,
          closeStrongAuthModal,
        });

        // Step 1: Execute mutation without verification first
        const initialResult = await mutateAsync(input);
        const initialChallenge = initialResult.error;

        const needsTotpCode = initialChallenge?.code === "totp_code_required";
        const needsSmsCode = initialChallenge?.code === "sms_code_required";
        const needsPassword = initialChallenge?.code === "strong_auth_required";

        if (!initialChallenge || !(needsTotpCode || needsSmsCode || needsPassword)) {
          return initialResult;
        }

        logAmplitudeEvent(AmplitudeEvents.TwoFAPrompted, { moment });

        if (needsPassword) {
          return await askForPassword();
        }

        return await askForSecondFactor(initialChallenge, input);
      } catch (error: any) {
        datadogLogs.logger.error("[useProtectByStrongAuth] Error during verification protected mutation", {
          error: serializeError(error),
        });

        return {
          data: undefined,
          error: {
            code: error?.shape?.data?.code || error?.code || "verification_error",
            message: error?.message || "An error occurred",
          },
        } as unknown as TData & { error?: { code: string; message?: string } };
      }
    },
    [openStrongAuthModal, closeStrongAuthModal, openTwoFactorModal, closeTwoFactorModal, logAmplitudeEvent, moment]
  );

  /**
   * Wrapper function that combines recaptcha and 2FA code handling
   * This ensures both recaptcha headers AND verification codes are properly passed to the mutation
   */
  const mutateWithSmsCodeAndRecaptcha = async <TData, TArgs extends Record<string, unknown>>(
    action: RecaptchaAction,
    email: string,
    mutationFn: (args: TArgs) => Promise<TData>,
    baseArgs: Omit<TArgs, "smsCode" | "password" | "googleSsoToken" | "totpCode" | "preferSms"> & {
      smsCode?: string | null;
      password?: string | null;
      googleSsoToken?: string | null;
      totpCode?: string | null;
      preferSms?: boolean | null;
    }
  ): Promise<TData & { isCancelled?: boolean }> => {
    const mutationWithRecaptcha = async (additionalArgs: Record<string, unknown>) => {
      const mergedArgs = { ...baseArgs, ...additionalArgs };
      return executeTrpc(action, email, mutationFn, mergedArgs as TArgs);
    };

    return mutateWithSmsCode(mutationWithRecaptcha, baseArgs) as unknown as Promise<TData & { isCancelled?: boolean }>;
  };

  return { mutateWithSmsCode, mutateWithSmsCodeAndRecaptcha };
};
